import React, { FC, useState } from "react";
import { Typography, Button, Box, makeStyles, withStyles, Theme, Tooltip, createStyles, TableBody } from "@material-ui/core";
import TableCell from "@material-ui/core/TableCell";
import Table from "@material-ui/core/Table";
import TableRow from "@material-ui/core/TableRow";
import { MyPortfolioProps } from "../../interfaces";
import ClaimTokenModal from "../Modals/ClaimTokenModal";
import { Colors } from "../../helpers/theme";
import usePortfolioListItemStyles from "./styles";
import { UserInvestmentRefundSchema } from "../../constants/user-investment";
import { checkCanRefund } from "./service";
import useModal from "../../hooks/useModal";
import ContributionRefundModal, { ContributionRefundPayload } from "../Modals/contribution/refund";
import { FaInfoCircle, FaRegHandPaper } from "react-icons/fa";
import DesktopRefundInfo from "./DesktopRefundInfo";
import { MdInfoOutline } from "react-icons/md";
import { useHistory } from "react-router";

const HtmlTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: '#11302D',
    fontSize: theme.typography.pxToRem(13),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

const useStyles = makeStyles(() => ({
  root: {
    overflow: "hidden",
    "& .MuiTableCell-root": {
      "&:first-child": {
        width: "15%",
      },
      "&:nth-child(2)": {
        width: "10%",
      },
      "&:nth-child(3)": {
        width: "10%",
      },
      "&:nth-child(4)": {
        width: "10%",
      },
      "&:nth-child(5)": {
        width: "8%",
      },
      "&:nth-child(6)": {
        width: "15%",
      },
      "&:nth-child(7)": {
        width: "10%",
      },
      "&:nth-child(8)": {
        width: "4%",
      },
      "&:nth-child(9)": {
        width: "18%",
      },
    },
  },
  project: {
    cursor: "pointer",
  },
  logo: {
    width: 30,
    height: 30,
    mixBlendMode: "normal",
    border: "3px solid #FFFFFF",
    borderRadius: "100px",
    filter: "drop-shadow(0px 4px 15px rgba(0, 0, 0, 0.1))",
    backgroundColor: Colors.white,
  },
  projectHeading: {
    fontSize: "14px",
    color: Colors.greenDark,
    paddingLeft: "5px",
  },
  projectTicker: {
    color: Colors.blackLight,
    fontWeight: 200,
    fontSize: "14px",
  },
  pendingButton: {
    padding: "5px",
  },
}));

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
        width: "100%",
        padding: 0,
        opacity: 0.8,
        borderRadius: "15px",
        boxShadow: "none",
        transition: "background-color 0.3s ease, transform 0.3s ease",
        willChange: "transform",
        '&:nth-of-type(odd)': {
            backgroundColor: Colors.darkWhite,
        },
        '&:nth-of-type(even)': {
          backgroundColor: Colors.white,
        },
        '&:hover': {
            backgroundColor: Colors.secondaryWhite,
            cursor: 'pointer',
            transform: "scale(1.02)",
        },
    },
  }),
)(TableRow);

const MyPortfolioDesktopList: FC<MyPortfolioProps> = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const portfolioClasses = usePortfolioListItemStyles();

  const {
    poolId,
    claimModalActionProps,
    poolTgeDate,
    poolRefundPolicy,
    investmentRefundPayload,
    distributionScAddress,
    distributionStatusDescription,
    totalTokenClaimable,
    totalTokenClaimed,
    isExternalProject,
    code,
  } = props;

  const [openClaimTokenModal, setOpenClaimTokenModal] = useState(false);
  const [investmentRefund, setInvestmentRefund] = useState<
    UserInvestmentRefundSchema | undefined
  >(investmentRefundPayload);
  const [showClaimBtn, setShowClaimBtn] = useState<boolean>(
    true && !investmentRefundPayload?.status
  );

  const [canRefund, setCanRefund] = useState(() =>
    checkCanRefund({
      poolTgeDate,
      investmentRefundPayload,
      poolRefundPolicy,
    })
  );
  const { status } = investmentRefund ?? {};


  const {
    close: closeRefundContributionModal,
    open: openRefundContributionModal,
    isOpen: isRefundContributionModalOpen,
    payload: refundContributionModalPayload,
  } = useModal();

  const handleClaimTokenModal = () => {
    setOpenClaimTokenModal(true);
  };

  const handleOnRefund = () => {
    openRefundContributionModal(props);
  };

  const onRefundRequestSuccess = (result: UserInvestmentRefundSchema) => {
    setCanRefund(false);
    setShowClaimBtn(false);
    setInvestmentRefund(result);
  };

  const isClaimable =
    props.distributionScAddress &&
    props.distributionScAddress.length > 0 &&
    props.distributionScAddress[0].isClaimable &&
    !status;

    const isClaimedDisabled: boolean = 
    !isClaimable ||
    (distributionScAddress && distributionScAddress[0].isAirdropped === true) ||
    (totalTokenClaimable !== undefined && totalTokenClaimable > 0 && 
     totalTokenClaimed !== undefined && totalTokenClaimed === totalTokenClaimable);

  const redirectToDetailPage = (event?: any) => {
    event?.preventDefault();
    history.push(`/project/${poolId}` + (isExternalProject ? `?code=${code}` : ''));
  }

  return (
    <>
      <StyledTableRow className={classes.root} onClick={() => redirectToDetailPage()}>
        {" "}
        {/** Flex Box main */}
        <TableCell align="left">
          <Box display="flex" flexDirection="row" alignItems="center" className={classes.project}>
            <img
              src={props?.imgSrc}
              alt="Project Image"
              className={classes.logo}
            />
            <Box
              display="flex"
              flexDirection="column"
            >
              <Typography
                variant="subtitle1"
                className={classes.projectHeading}
              >
                {props?.title}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell align="left">
          <Typography variant="subtitle1" color="secondary">
            {props.status.toUpperCase()}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="subtitle1" className="fw-bold" color="primary">
            {props.contr}
          </Typography>
        </TableCell>
        <TableCell align="left">
          <Typography variant="subtitle1"  className="fw-bold" color="secondary">
            {props.address === null
              ? "NA"
              : props.address.length > 5
              ? props.address.substr(0, 5) +
                "..." +
                props.address.substr(
                  props.address.length - 7,
                  props.address.length
                )
              : props.address}
          </Typography>
        </TableCell>
        <TableCell align="left">
          <Typography variant="subtitle1" className="fw-bold" color="primary">
            {props.price}
          </Typography>
        </TableCell>
        <TableCell align="left">
          <Typography variant="subtitle1" className="fw-bold" color="primary">
            {props.tokenReceived}
          </Typography>
        </TableCell>
        <TableCell align="left">
          <Typography variant="subtitle1" className="fw-bold" color="primary">
            {Number(props.percent) ? (props.percent > 0 ? "+" : "") + props.percent + "%" : "Not Listed"}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <HtmlTooltip
            title={
              <React.Fragment>
                <DesktopRefundInfo poolRefundPolicy={poolRefundPolicy} poolTgeDate={poolTgeDate} />
              </React.Fragment>
            }
          >
            <div style={{ display: 'inline-block', cursor: 'not-allowed', width: '100%' }}>
              <FaInfoCircle />
            </div>
          </HtmlTooltip>
        </TableCell>
        <TableCell align="center">
          <div
            style={{
              display: 'flex', // This container will hold both buttons and the tooltip in a row
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '10px', // Provide space between buttons and tooltip
              width: '100%', // Ensure the entire row takes full width
            }}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column', // Stack buttons vertically
              alignItems: 'stretch', // Make sure buttons take full width
              gap: '10px', // Set gap between buttons
              flex: 1, // Allow buttons to take up available space
            }}>
              {showClaimBtn && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={
                    (event) => {
                      event.stopPropagation();
                      handleClaimTokenModal();
                    }
                  }
                  disabled={isClaimedDisabled}
                  fullWidth
                >
                  {distributionScAddress && distributionScAddress[0].isAirdropped ? "Airdropped" : "Claim"}
                </Button>
              )}
              {canRefund && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={
                    (event) => {
                      event.stopPropagation();
                      handleOnRefund();
                    }
                  }
                  fullWidth     
                >
                  <FaRegHandPaper />&nbsp;
                  Request Refund
                </Button>
              )}
              {status === "requested" && (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled
                  className={classes.pendingButton}
                >
                  Pending refund
                </Button>
              )}
              {status === "refunded" && (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled
                  className={portfolioClasses.refunded_btn}
                >
                  Refunded
                </Button>
              )}
            </div>
            {
              distributionStatusDescription && (
                <div
                  style={{
                    marginTop: '10px', // Provide some spacing between the buttons and the tooltip if needed
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Tooltip title={distributionStatusDescription}>
                    <span>
                      <MdInfoOutline style={{ color: 'inherit' }} />
                    </span>
                  </Tooltip>
                </div>
              )
            }
          </div>
        </TableCell>
      </StyledTableRow>
      {
        openClaimTokenModal ? (
          <ClaimTokenModal
            open={openClaimTokenModal}
            setOpen={setOpenClaimTokenModal}
            poolId={props.poolId}
            canRefund={canRefund}
            modalActionProps={claimModalActionProps}
          />
        ) : (
          ""
        )
      }
      <ContributionRefundModal
        onClose={closeRefundContributionModal}
        isOpen={isRefundContributionModalOpen}
        payload={refundContributionModalPayload as ContributionRefundPayload}
        onSuccess={onRefundRequestSuccess}
      />
    </>
  );
};

export default MyPortfolioDesktopList;
